import * as React from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import { alpha } from '@mui/material/styles';

import apiClient, { shouldRedirectToLogin, getErrorMessage } from '../services/api-client';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Navigate, useNavigate } from 'react-router-dom';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import TextField from '@mui/material/TextField';

import AppConfig from '../services/app-config';
import AuthService from '../services/auth.js';
import notificationService from '../services/notification-service.js';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import { ColorModeContext } from '../ColorModeContext';
import PageHeader from '../components/PageHeader';

export default function Join({setNavbarState, navbarState}) {
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' })
  const [loadingPreview, setLoadingPreview] = React.useState(true)
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = React.useState('warning');
  const [notificationsEnabled, setNotificationsEnabled] = React.useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted' && AppConfig.getLocal('notification_enable') === true
  );
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [userData, setUserData] = React.useState([]);
  const navigate = useNavigate();
  const colorMode = React.useContext(ColorModeContext);
  const [savingProfile, setSavingProfile] = React.useState(false);
  const [profileLoaded, setProfileLoaded] = React.useState(false);
  const [profileDirty, setProfileDirty] = React.useState(false);
  const autosaveTimeoutRef = React.useRef(null);
  const formDataRef = React.useRef(null);
  const [formData, setFormData] = React.useState({
    name: '',
    lastname: '',
    phone: '',
    appear_in_directory: false,
  });

  React.useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  React.useEffect(() => {
    setNavbarState({...navbarState, showBack: true, showOptions: false, showSearch: false})
    apiClient.get('users/me', {}, { action: 'fetch_user_profile' })
      .then((response) => {
        if (response.status === 200) {
          setUserData(response.data.data);
          setFormData({
            name: response.data.data?.name || '',
            lastname: response.data.data?.lastname || '',
            phone: response.data.data?.phone || '',
            appear_in_directory: Boolean(response.data.data?.appear_in_directory),
          });
          setProfileLoaded(true);
          setProfileDirty(false);
          setLoadingPreview(false);
        } else if (response.status === 401) {
          setRedirect({ trigger: true, route: '/login' });
        }
      })
      .catch((error) => {
        setRedirect({ trigger: true, route: '/login' });
      })
  }, []);

  const saveProfile = React.useCallback(async () => {
    if (!profileLoaded) {
      return;
    }
    if (!profileDirty) {
      return;
    }
    if (savingProfile) {
      return;
    }

    const payload = { ...(formData || {}) };
    setSavingProfile(true);
    try {
      const response = await apiClient.post('users/update/me', payload, {}, { action: 'update_user_profile' });
      AppConfig.parseResponse(response);
      if (response.status === 200) {
        setUserData((prev) => ({ ...(prev || {}), ...(payload || {}) }));
        setProfileDirty((prevDirty) => {
          if (!prevDirty) {
            return false;
          }
          const current = formDataRef.current;
          const stillSame = current ? JSON.stringify(payload) === JSON.stringify(current) : true;
          return stillSame ? false : true;
        });
        setSnackbarSeverity('success');
        setSnackbarMessage('Cambios guardados');
        setSnackbarOpen(true);
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage('No se pudieron guardar los cambios');
        setSnackbarOpen(true);
      }
    } catch (error) {
      if (shouldRedirectToLogin(error)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(getErrorMessage(error) || 'Error al guardar cambios');
      setSnackbarOpen(true);
    } finally {
      setSavingProfile(false);
    }
  }, [formData, profileDirty, profileLoaded, savingProfile]);

  React.useEffect(() => {
    if (!profileLoaded || !profileDirty) {
      return undefined;
    }

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = setTimeout(() => {
      saveProfile();
    }, 750);

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [formData, profileDirty, profileLoaded, saveProfile]);
  
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const [loading, setLoading] = React.useState(false);

  const handleLogout = React.useCallback(async () => {
    setLoading(true);
    try {
      await notificationService.unregisterToken();
    } catch (error) {
      // ignore, continue logout
    } finally {
      AuthService.logout();
      setRedirect({ trigger: true, route: '/login' });
      setLoading(false);
    }
  }, []);

  const requestNotificationPermission = React.useCallback(async () => {
    setLoading(true);
    try {
      const success = await notificationService.requestPermissionAndRegister();
      if (success) {
        setNotificationsEnabled(true);
        setSnackbarSeverity('success');
        setSnackbarMessage('Notificaciones habilitadas correctamente');
        setSnackbarOpen(true);
      } else {
        setSnackbarSeverity('warning');
        setSnackbarMessage('Permiso de notificaciones denegado');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage('Error al habilitar notificaciones');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleNotificationToggle = (event) => {
    if (event.target.checked) {
      requestNotificationPermission();
    } else {
      unregisterNotifications();
    }
  }

  const unregisterNotifications = async () => {
    setLoading(true);
    try {
      await notificationService.unregisterToken();
      setNotificationsEnabled(false);
      setSnackbarSeverity('success');
      setSnackbarMessage('Notificaciones deshabilitadas');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error disabling notifications:', error);
      setNotificationsEnabled(false);
      setSnackbarSeverity('error');
      setSnackbarMessage('Error al deshabilitar notificaciones');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }


  const renderUnit = () => {
    if (userData.role === "receptor" || userData.role === "admin") {
      return "Unidad"
    } else {
      return AppConfig.get('unit_type')
    }
  }

  const fullName = React.useMemo(() => {
    const n = (formData.name || userData.name || '').trim();
    const l = (formData.lastname || userData.lastname || '').trim();
    return [n, l].filter(Boolean).join(' ');
  }, [formData.name, formData.lastname, userData.name, userData.lastname]);

  const initials = React.useMemo(() => {
    const first = (formData.name || userData.name || '').trim().charAt(0);
    const last = (formData.lastname || userData.lastname || '').trim().charAt(0);
    return (first + last).toUpperCase() || '?';
  }, [formData.name, formData.lastname, userData.name, userData.lastname]);

  const cardSx = {
    borderRadius: '10px',
    boxShadow: 0,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
  };

  const sectionTitleSx = {
    fontSize: '1.125rem',
    fontWeight: 800,
    color: 'text.primary',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  };

  const switchRowSx = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 2,
    width: '100%',
  };

  return (
    <Container maxWidth="sm" disableGutters>
      {redirect.trigger && (
        <Navigate to={redirect.route} replace={true} />
      )}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: "top", horizontal: "center" }} sx={{mt: "3.2rem"}}>
        <Alert onClose={handleClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 6 }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            px: 3,
            pt: 2,
            pb: 1.5,
            backgroundColor: 'background.paper',
          }}
        >
          <PageHeader
            title="Mi Perfil"
            icon="user-rounded"
            onBack={() => navigate(-1)}
            right={profileLoaded && (savingProfile || profileDirty) ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '10px',
                  color: 'text.secondary',
                }}
              >
                {savingProfile ? (
                  <>
                    <CloudSyncRoundedIcon sx={{ fontSize: 15, color: 'secondary.main' }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'secondary.main' }}>Guardando…</Typography>
                  </>
                ) : (
                  <>
                    <CloudSyncRoundedIcon sx={{ fontSize: 15 }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Pendiente</Typography>
                  </>
                )}
              </Box>
            ) : null}
          />
        </Box>

        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loadingPreview || loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        {!loadingPreview &&
          (
            <Box sx={{ px: 3, mt: 1.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Avatar header card */}
              <Card sx={{ ...cardSx, overflow: 'visible' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 88,
                      height: 88,
                      bgcolor: 'secondary.main',
                      color: '#ffffff',
                      fontSize: 32,
                      fontWeight: 900,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 900, color: 'text.primary', lineHeight: 1.2 }}>
                      {fullName || '—'}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary', mt: 0.25 }}>
                      {userData.username || ''}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Card 1: Información Personal */}
              <Box data-tour="profile-fields">
                <Card sx={cardSx}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack spacing={2.5}>
                      <Typography sx={sectionTitleSx}>
                        <PersonOutlineRoundedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                        Información Personal
                      </Typography>
                      <TextField
                        fullWidth
                        size="medium"
                        type="text"
                        id="name"
                        value={formData.name}
                        label="Nombre"
                        variant="outlined"
                        onChange={(e) => {
                          setFormData({ ...formData, name: e.target.value });
                          setProfileDirty(true);
                        }}
                      />
                      <TextField
                        fullWidth
                        size="medium"
                        type="text"
                        id="lastname"
                        value={formData.lastname}
                        label="Apellido"
                        variant="outlined"
                        onChange={(e) => {
                          setFormData({ ...formData, lastname: e.target.value });
                          setProfileDirty(true);
                        }}
                      />
                      <TextField
                        fullWidth
                        size="medium"
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        label="Número telefónico"
                        variant="outlined"
                        onChange={(e) => {
                          setFormData({ ...formData, phone: e.target.value });
                          setProfileDirty(true);
                        }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Box>

              {/* Card 2: Configuración */}
              <Card sx={cardSx}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack spacing={2.5}>
                    <Typography sx={sectionTitleSx}>
                      <SettingsRoundedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                      Configuración
                    </Typography>

                    <Box data-tour="profile-notifications" sx={switchRowSx}>
                      <Box sx={{ pr: 2 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}>
                          Recibir notificaciones
                        </Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 500, color: 'text.secondary', mt: 0.25 }}>
                          Recibe avisos push de visitas, paquetes y novedades.
                        </Typography>
                        {AuthService.userRoleIncludes([]) && notificationsEnabled && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                            {AppConfig.getLocal('notification_token_id')}
                          </Typography>
                        )}
                      </Box>
                      <Switch
                        checked={notificationsEnabled}
                        onChange={handleNotificationToggle}
                        name="receiveNotifications"
                        color="secondary"
                      />
                    </Box>

                    <Divider />

                    <Box data-tour="profile-darkmode" sx={switchRowSx}>
                      <Box sx={{ pr: 2 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}>
                          Modo oscuro
                        </Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 500, color: 'text.secondary', mt: 0.25 }}>
                          Cambia la apariencia de la app a un tema oscuro.
                        </Typography>
                      </Box>
                      <Switch
                        checked={colorMode.mode === 'dark'}
                        onChange={colorMode.toggleColorMode}
                        name="darkMode"
                        color="secondary"
                      />
                    </Box>

                    {userData.role !== "receptor" && AppConfig.get("modules").includes("directory") ? (
                      <>
                        <Divider />
                        <Box sx={switchRowSx}>
                          <Box sx={{ pr: 2 }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}>
                              Aparecer en directorio
                            </Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 500, color: 'text.secondary', mt: 0.25 }}>
                              Permite que otros residentes te encuentren en el directorio.
                            </Typography>
                          </Box>
                          <Switch
                            checked={Boolean(formData.appear_in_directory)}
                            onChange={(e) => {
                              setFormData({ ...formData, appear_in_directory: e.target.checked });
                              setProfileDirty(true);
                            }}
                            name="appearInDirectory"
                            color="secondary"
                          />
                        </Box>
                      </>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>

              {/* Card 3: Cuenta */}
              <Card sx={cardSx}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack spacing={2.5}>
                    <Typography sx={sectionTitleSx}>
                      <LockOutlinedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                      Cuenta
                    </Typography>
                    <TextField
                      disabled
                      fullWidth
                      size="medium"
                      type="email"
                      id="mail"
                      value={userData.username || ''}
                      label="Correo electrónico"
                      variant="outlined"
                      helperText="Contacte al administrador para cambiar su correo."
                      FormHelperTextProps={{
                        sx: {
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color: 'text.primary',
                          '&.Mui-disabled': { color: 'text.primary' },
                        }
                      }}
                    />
                    <TextField
                      disabled
                      fullWidth
                      value={userData.unit || ''}
                      size="medium"
                      type="text"
                      id="apartment"
                      label={renderUnit()}
                      variant="outlined"
                    />
                  </Stack>
                </CardContent>
              </Card>

              {/* Logout (mobile only) */}
              <Button
                fullWidth
                size="large"
                variant="outlined"
                onClick={handleLogout}
                sx={{
                  py: 1.2,
                  borderRadius: '16px',
                  textTransform: 'none',
                  fontWeight: 800,
                  color: 'text.primary',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'divider',
                    backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04),
                  },
                  display: { xs: 'flex', sm: 'none' },
                }}
              >
                Cerrar sesión
              </Button>
            </Box>
          )
        }
      </Box>
    </Container>
  );
}
